'use client';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, TrendingUp, Users, Zap, Shield, Brain, BarChart3, Download } from 'lucide-react';
import gsap from 'gsap';

const slides = [
  'cover',
  'problem',
  'solution',
  'how-it-works',
  'data-import',
  'features',
  'roi',
  'implementation',
  'contact',
];

const slideNames = {
  cover: 'ManagEaze',
  problem: 'Your Challenge',
  solution: 'Our Solution',
  'how-it-works': 'How It Works',
  'data-import': 'Data Import',
  features: 'Features',
  roi: 'Business Impact',
  implementation: 'Implementation & Support',
  contact: "Let's Talk",
};

const colors = {
  navy: '#07081a',
  navyCard: '#11154a',
  purple: '#6C63FF',
  purpleDim: '#4B44CC',
  lime: '#a8e063',
  white: '#ffffff',
  slate: '#c4c9e8',
  dimSlate: '#7880a8',
  border: 'rgba(108,99,255,0.2)',
  glassBg: 'rgba(11,13,40,0.7)',
};

// Logo Image
const LogoImage = ({ size = 8 }: { size?: number }) => (
  <img
    src="/logo.svg"
    alt="ManagEaze Logo"
    className={`w-${size} h-${size}`}
    style={{ width: `${size * 0.25}in`, height: `${size * 0.25}in` }}
  />
);

// Slide Header with Logo
const SlideHeader = ({ title }: { title: string }) => (
  <div className="flex items-center gap-4 mb-12">
    <img
      src="/logo.svg"
      alt="ManagEaze Logo"
      style={{ width: '48px', height: '48px' }}
    />
    <h2 className="text-6xl font-black" style={{ color: colors.white, fontFamily: 'DM Sans' }}>
      {title}
    </h2>
  </div>
);

// Floating Orbs with GSAP
const FloatingOrbs = () => {
  const orb1Ref = useRef(null);
  const orb2Ref = useRef(null);
  const orb3Ref = useRef(null);

  useEffect(() => {
    if (orb1Ref.current) {
      gsap.to(orb1Ref.current, {
        duration: 4,
        y: -30,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }
    if (orb2Ref.current) {
      gsap.to(orb2Ref.current, {
        duration: 5,
        y: 30,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 0.5,
      });
    }
    if (orb3Ref.current) {
      gsap.to(orb3Ref.current, {
        duration: 6,
        y: -20,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 1,
      });
    }
  }, []);

  return (
    <>
      <div
        ref={orb1Ref}
        className="absolute top-20 left-20 w-64 h-64 rounded-full opacity-30 blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.purple}, transparent)`,
        }}
      />
      <div
        ref={orb2Ref}
        className="absolute bottom-32 right-32 w-72 h-72 rounded-full opacity-20 blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.lime}, transparent)`,
        }}
      />
      <div
        ref={orb3Ref}
        className="absolute top-1/2 right-20 w-80 h-80 rounded-full opacity-25 blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.purpleDim}, transparent)`,
        }}
      />
    </>
  );
};

// Dot Grid Background
const DotGrid = () => (
  <div
    className="absolute inset-0 opacity-40"
    style={{
      backgroundImage: `radial-gradient(circle, ${colors.slate} 1px, transparent 1px)`,
      backgroundSize: '30px 30px',
      maskImage: `radial-gradient(circle at center, black 0%, transparent 100%)`,
      WebkitMaskImage: `radial-gradient(circle at center, black 0%, transparent 100%)`,
    }}
  />
);

// Slide 1: Cover
const CoverSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center overflow-hidden"
    style={{ backgroundColor: colors.navy }}
  >
    <FloatingOrbs />
    <DotGrid />
    <div className="relative z-10 text-center px-8 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 flex justify-center items-center gap-3"
      >
        <img
          src="/logo.svg"
          alt="ManagEaze Logo"
          style={{ width: '64px', height: '64px' }}
        />
        <span className="text-3xl font-black" style={{ color: colors.white, fontFamily: 'DM Sans' }}>
          ManagEaze
        </span>
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-7xl md:text-8xl font-black tracking-tighter mb-6 leading-tight"
      >
        Govern Smarter.
        <br />
        Comply Faster.
        <br />
        <span
          style={{
            backgroundImage: `linear-gradient(90deg, ${colors.purple}, ${colors.lime})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Scale Without Chaos.
        </span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-xl mb-8"
        style={{ color: colors.dimSlate }}
      >
        Transform your policy management. Reduce compliance risk. Drive business efficiency.
      </motion.p>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="px-8 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90"
        style={{ backgroundColor: colors.lime, color: colors.navy }}
      >
        Let's Talk
      </motion.button>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-8 flex justify-center"
      >
        <div className="px-4 py-3 bg-white rounded-lg inline-block">
          <img
            src="/nvidia-inception.png"
            alt="NVIDIA Inception Program"
            className="h-8 w-auto"
          />
        </div>
      </motion.div>
    </div>
  </section>
);

// Slide 2: Problem
const ProblemSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="Your Challenge" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="text-3xl font-black mb-8" style={{ color: colors.white }}>
          Policy management shouldn't be this complicated
        </p>
        <p className="text-xl" style={{ color: colors.slate }}>
          Are your policies actually being followed? Can you prove compliance when audited?
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            title: 'Static Documents',
            desc: 'Policies sit in archives, outdated & unenforceable',
            icon: '📄',
          },
          {
            title: 'Fragmented Tools',
            desc: '40% of audit findings stem from policy gaps',
            icon: '🔀',
          },
          {
            title: 'Zero Visibility',
            desc: '87% of orgs struggle with effective policy management',
            icon: '👁️',
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl border"
            style={{
              backgroundColor: colors.glassBg,
              borderColor: colors.border,
            }}
          >
            <div className="text-5xl mb-4">{item.icon}</div>
            <h3 className="text-2xl font-bold mb-2" style={{ color: colors.white }}>
              {item.title}
            </h3>
            <p style={{ color: colors.dimSlate }}>{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Slide 3: Solution
const SolutionSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="Our Solution" />
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h3 className="text-4xl font-black mb-6" style={{ color: colors.white }}>
            One platform. Complete control.
          </h3>
          <p className="text-lg mb-8" style={{ color: colors.slate }}>
            ManagEaze transforms policy from static documents into executable, monitored processes that keep your organization compliant, efficient, and audit-ready.
          </p>
          <div className="space-y-4">
            {[
              'AI-powered policy creation tailored to your region',
              'Real-time compliance monitoring & enforcement',
              'Audit-ready documentation — always',
              'Weeks to deploy, not months',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 size={24} style={{ color: colors.lime }} />
                <span style={{ color: colors.slate }}>{item}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="rounded-2xl border overflow-hidden"
          style={{
            borderColor: colors.border,
          }}
        >
          <img
            src="/product-screenshot.png"
            alt="ManagEaze Dashboard"
            className="w-full h-auto"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

// Slide 4: About (REMOVED FOR SALES DECK - MOVED TO SOLUTION SLIDE)

// Slide 5: How It Works
const HowItWorksSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="How It Works" />

      <div className="flex flex-col md:flex-row justify-between relative">
        {[
          { icon: '📚', title: 'Knowledge', desc: 'Ingest & organize company context' },
          { icon: '✍️', title: 'Create', desc: 'AI-powered policy generation' },
          { icon: '👁️', title: 'Review', desc: 'Smart approval workflows' },
          { icon: '✅', title: 'Approve', desc: 'Audit-ready tracking' },
          { icon: '📊', title: 'Monitor', desc: 'Real-time compliance visibility' },
        ].map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            viewport={{ once: true }}
            className="flex-1 text-center relative"
          >
            <div className="text-5xl mb-4">{step.icon}</div>
            <h3 className="text-xl font-bold mb-2" style={{ color: colors.white }}>
              {step.title}
            </h3>
            <p className="text-sm" style={{ color: colors.dimSlate }}>
              {step.desc}
            </p>
            {i < 4 && (
              <div
                className="absolute top-12 -right-4 md:block hidden"
                style={{ color: colors.border }}
              >
                <ArrowRight size={24} />
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Slide: Data Import / Onboarding existing documents
const DataImportSlide = () => {
  const files = [
    { name: 'Information_Security_Policy.pdf', status: 'done' },
    { name: 'Code_of_Conduct.docx', status: 'done' },
    { name: 'Data_Retention_2021.doc', status: 'converting' },
    { name: 'Remote_Work_Guidelines.pdf', status: 'queued' },
    { name: 'Vendor_Risk_Policy.docx', status: 'queued' },
  ];

  const statusMeta: Record<string, { label: string; color: string }> = {
    done: { label: 'Policy created ✓', color: colors.lime },
    converting: { label: 'AI converting…', color: colors.purple },
    queued: { label: 'Queued', color: colors.dimSlate },
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center px-8"
      style={{ backgroundColor: colors.navy }}
    >
      <div className="max-w-6xl w-full">
        <SlideHeader title="Onboard in Days, Not Months" />

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-black mb-6" style={{ color: colors.white }}>
              Bring your existing documents.
            </h3>
            <p className="text-lg mb-8" style={{ color: colors.slate }}>
              No re-typing. No reformatting. Drag in the policies you already have — PDF, Word, or text — and our AI converts each one into a clean, structured, fully editable policy in your library.
            </p>
            <div className="space-y-4">
              {[
                'Batch import up to 50 documents at once',
                'AI extracts headings, sections & lists — no manual cleanup',
                'Auto-titled and saved as ready-to-review drafts',
                'From legacy archive to living policy in minutes',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={20} style={{ color: colors.lime }} />
                  <span style={{ color: colors.slate }}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl border"
            style={{ backgroundColor: colors.glassBg, borderColor: colors.border }}
          >
            <div className="text-xs font-semibold mb-3" style={{ color: colors.slate }}>
              Batch Import — converting your archive
            </div>
            <div className="space-y-2">
              {files.map((file, i) => {
                const meta = statusMeta[file.status];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.12 }}
                    viewport={{ once: true }}
                    className="p-3 rounded-lg flex items-center gap-3"
                    style={{
                      backgroundColor: 'rgba(108,99,255,0.1)',
                      borderLeft: `3px solid ${meta.color}`,
                    }}
                  >
                    <span className="text-lg">📄</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate" style={{ color: colors.white }}>
                        {file.name}
                      </div>
                      <div className="text-xs" style={{ color: meta.color }}>
                        {meta.label}
                      </div>
                    </div>
                    {file.status === 'converting' && (
                      <motion.div
                        className="w-4 h-4 rounded-full border-2"
                        style={{ borderColor: colors.purple, borderTopColor: 'transparent' }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
            <div
              className="p-2 rounded text-xs mt-3 text-center"
              style={{ backgroundColor: 'rgba(168,224,99,0.15)', color: colors.lime }}
            >
              ✓ 2 of 5 converted into editable policies
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Demo Components for Features
const KnowledgeBaseDemo = () => (
  <div className="space-y-4">
    <div className="text-xs font-semibold mb-3" style={{ color: colors.slate }}>
      Regulatory Sources Ingested
    </div>
    <div className="space-y-2">
      {[
        { doc: 'GDPR Regulations (EU)', region: '🇪🇺 Europe', icon: '📋' },
        { doc: 'CCPA Guidelines (US)', region: '🇺🇸 California', icon: '📋' },
        { doc: 'HIPAA Requirements', region: '🏥 Healthcare', icon: '📋' },
        { doc: 'SOC 2 Compliance Framework', region: '🔒 Security', icon: '📋' },
        { doc: 'Company Internal Policies', region: '🏢 Internal', icon: '📄' },
      ].map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.08 }}
          className="p-3 rounded-lg"
          style={{
            backgroundColor: 'rgba(108,99,255,0.1)',
            borderLeft: `3px solid ${item.region.includes('🏢') ? colors.dimSlate : colors.lime}`,
          }}
        >
          <div className="flex items-start gap-3">
            <span className="text-lg">{item.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold" style={{ color: colors.white }}>
                {item.doc}
              </div>
              <div className="text-xs" style={{ color: colors.dimSlate }}>
                {item.region}
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
    <div
      className="p-2 rounded text-xs mt-3"
      style={{
        backgroundColor: 'rgba(168,224,99,0.15)',
        color: colors.lime,
      }}
    >
      ✓ AI learns from regulations to auto-generate compliant policies
    </div>
  </div>
);

const PolicyWorkflowDemo = () => (
  <div className="space-y-3">
    {[
      { status: 'Draft', count: 3, color: colors.dimSlate },
      { status: 'Under Review', count: 5, color: colors.purple },
      { status: 'Approved', count: 12, color: colors.lime },
    ].map((col, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: i * 0.12 }}
        className="p-3 rounded-lg border"
        style={{
          backgroundColor: 'rgba(108,99,255,0.1)',
          borderColor: col.color,
          borderLeftWidth: '3px',
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold" style={{ color: col.color }}>
              {col.status}
            </div>
            <div className="text-xs" style={{ color: colors.dimSlate }}>
              {col.count} policies
            </div>
          </div>
          <div
            className="px-3 py-1 rounded-full text-xs font-bold"
            style={{ backgroundColor: col.color, color: colors.navy }}
          >
            {col.count}
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

const QuizDemo = () => (
  <div className="space-y-4">
    <div>
      <div className="text-sm font-semibold mb-2" style={{ color: colors.white }}>
        Security Policy Quiz
      </div>
      <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
        <motion.div
          className="h-full"
          initial={{ width: 0 }}
          animate={{ width: '60%' }}
          transition={{ duration: 2 }}
          style={{ backgroundColor: colors.lime }}
        />
      </div>
      <div className="text-xs mt-2" style={{ color: colors.dimSlate }}>
        6 of 10 questions answered
      </div>
    </div>
    <div
      className="p-4 rounded-lg border"
      style={{ backgroundColor: colors.glassBg, borderColor: colors.border }}
    >
      <div className="text-sm font-semibold mb-3" style={{ color: colors.white }}>
        What is the first step in incident response?
      </div>
      <div className="space-y-2">
        {['Report to compliance', 'Isolate affected systems', 'Document details'].map((opt, i) => (
          <motion.button
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}
            className="w-full p-2 rounded text-sm text-left transition-colors"
            style={{
              backgroundColor: i === 1 ? colors.lime : 'rgba(108,99,255,0.1)',
              color: i === 1 ? colors.navy : colors.slate,
            }}
          >
            {opt}
          </motion.button>
        ))}
      </div>
    </div>
  </div>
);

const AIAgentDemo = () => {
  const [messages, setMessages] = useState<Array<{ role: string; text: string }>>([
    { role: 'user', text: 'What is our data retention policy?' },
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'According to our Data Privacy Policy, we retain personal data for 3 years after the last interaction...',
        },
      ]);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-3 h-80 overflow-y-auto">
      {messages.map((msg, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`text-sm p-3 rounded-lg ${msg.role === 'user' ? 'ml-8' : 'mr-8'
            }`}
          style={{
            backgroundColor:
              msg.role === 'user'
                ? 'rgba(108,99,255,0.3)'
                : 'rgba(168,224,99,0.15)',
            color: colors.slate,
          }}
        >
          {msg.text}
        </motion.div>
      ))}
    </div>
  );
};

const ComplianceDemo = () => (
  <div className="space-y-4">
    <div className="space-y-2">
      {[
        { name: 'SOC 2 Type II', controls: '34 / 41', pct: 83, audit: 'Audit Q3' },
        { name: 'ISO 27001', controls: '52 / 93', pct: 56, audit: 'In progress' },
        { name: 'HIPAA', controls: '18 / 22', pct: 82, audit: 'Audit Q4' },
      ].map((prog, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          className="p-3 rounded-lg border"
          style={{ backgroundColor: 'rgba(108,99,255,0.1)', borderColor: colors.border }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: colors.white }}>
              {prog.name}
            </span>
            <span className="text-xs" style={{ color: colors.dimSlate }}>
              {prog.controls} controls · {prog.audit}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full"
              initial={{ width: 0 }}
              animate={{ width: `${prog.pct}%` }}
              transition={{ duration: 1.2, delay: i * 0.1 }}
              style={{ backgroundColor: colors.lime }}
            />
          </div>
        </motion.div>
      ))}
    </div>
    <div
      className="p-2 rounded text-xs"
      style={{ backgroundColor: 'rgba(168,224,99,0.15)', color: colors.lime }}
    >
      ✓ Evidence collected & shared with auditors through a dedicated portal
    </div>
  </div>
);

const GovernanceDemo = () => (
  <div className="space-y-4">
    <div>
      <div className="text-xs font-semibold mb-3" style={{ color: colors.slate }}>
        Committee Structure
      </div>
      <div className="space-y-2">
        {[
          { name: 'Board of Directors', members: 8 },
          { name: 'Audit Committee', members: 5 },
          { name: 'Risk Committee', members: 4 },
        ].map((committee, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="p-2 rounded text-xs flex items-center justify-between"
            style={{
              backgroundColor: 'rgba(108,99,255,0.1)',
              color: colors.slate,
            }}
          >
            <span>{committee.name}</span>
            <span
              className="px-2 py-1 rounded"
              style={{ backgroundColor: colors.purple, color: colors.white }}
            >
              {committee.members}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
    <div
      className="p-3 rounded-lg text-center border"
      style={{ backgroundColor: 'rgba(168,224,99,0.1)', borderColor: colors.lime }}
    >
      <div className="text-xs font-semibold mb-1" style={{ color: colors.lime }}>
        Latest Vote
      </div>
      <div className="text-xs" style={{ color: colors.slate }}>
        Risk Appetite Statement — Approved ✓
      </div>
    </div>
  </div>
);

const RiskIntelligenceDemo = () => {
  const riskMatrix = [
    [2, 3, 4, 5, 3],
    [1, 3, 5, 4, 2],
    [2, 4, 5, 5, 4],
    [1, 2, 3, 4, 3],
    [0, 1, 2, 3, 2],
  ];

  const getRiskColor = (value: number) => {
    if (value >= 5) return '#FF6B6B';
    if (value >= 4) return '#FFB93C';
    if (value >= 3) return '#FFC837';
    if (value >= 2) return '#A8E063';
    return '#5A8C51';
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-semibold mb-3" style={{ color: colors.slate }}>
          Risk Heatmap
        </div>
        <div className="space-y-1">
          {riskMatrix.map((row, rowIdx) => (
            <div key={rowIdx} className="flex gap-1">
              {row.map((cell, colIdx) => (
                <motion.div
                  key={`${rowIdx}-${colIdx}`}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: (rowIdx * 5 + colIdx) * 0.04,
                    duration: 0.3,
                  }}
                  className="flex-1 aspect-square rounded-sm border"
                  style={{
                    backgroundColor: getRiskColor(cell),
                    borderColor: 'rgba(255,255,255,0.1)',
                    opacity: 0.8,
                  }}
                  title={`Risk Level: ${cell}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs px-2">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: '#5A8C51' }}
          />
          <span style={{ color: colors.dimSlate }}>Low</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: '#FF6B6B' }}
          />
          <span style={{ color: colors.dimSlate }}>Critical</span>
        </div>
      </div>

      <div
        className="p-2 rounded text-xs"
        style={{
          backgroundColor: 'rgba(255,107,107,0.1)',
          color: colors.slate,
        }}
      >
        <span style={{ color: '#FF6B6B' }} className="font-semibold">
          3 Critical
        </span>{' '}
        risks identified — Immediate action required
      </div>
    </div>
  );
};

// Slide 6: Features (with tabs)
const FeaturesSlide = ({ printAll = false }: { printAll?: boolean }) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      label: 'Knowledge Base',
      icon: '📚',
      copy: 'Ingest regional regulations, industry guidelines, and internal policies. AI learns your compliance requirements to auto-generate tailored policies and guide your AI Agent.',
      bullets: ['Region-specific regulations', 'Industry compliance frameworks', 'Powers AI-driven policy generation'],
      demo: <KnowledgeBaseDemo />,
    },
    {
      label: 'Policy Workflow',
      icon: '⚡',
      copy: 'A rich policy editor with intelligent approval workflows, embedded comments, custom reviewer roles, and a complete audit trail of every change.',
      bullets: ['Rich-text editor & version control', 'Smart routing & approvals', 'Renewal tracking & full audit trail'],
      demo: <PolicyWorkflowDemo />,
    },
    {
      label: 'Quizzes',
      icon: '🎯',
      copy: 'AI-generated quizzes to ensure policy acknowledgment and employee engagement.',
      bullets: ['Auto-generated content', 'Progress tracking', 'Analytics'],
      demo: <QuizDemo />,
    },
    {
      label: 'AI Agent',
      icon: '🤖',
      copy: 'Conversational AI that answers policy questions and provides instant compliance guidance.',
      bullets: ['24/7 availability', 'Context-aware', 'Multi-language support'],
      demo: <AIAgentDemo />,
    },
    {
      label: 'Compliance',
      icon: '✅',
      copy: 'Run framework-based compliance programs — SOC 2 Type II, ISO 27001, and HIPAA — with mapped controls, evidence collection, and a dedicated portal for external auditors.',
      bullets: ['SOC 2, ISO 27001 & HIPAA programs', 'Evidence repository & control tracking', 'Dedicated auditor portal'],
      demo: <ComplianceDemo />,
    },
    {
      label: 'Governance',
      icon: '🏛️',
      copy: 'Org chart visualization, committee management, and electronic voting for decisions.',
      bullets: ['Committee workflows', 'Decision tracking', 'Audit trails'],
      demo: <GovernanceDemo />,
    },
    {
      label: 'Risk Intelligence',
      icon: '⚠️',
      copy: 'Identify, assess, and monitor enterprise risks in real-time. Visualize risk landscapes and track mitigation progress.',
      bullets: ['Risk heatmap visualization', 'Risk register tracking', 'Mitigation workflows'],
      demo: <RiskIntelligenceDemo />,
    },
  ];

  return (
    <section
      className="relative min-h-screen flex items-center justify-center px-8"
      style={{ backgroundColor: colors.navy }}
    >
      <div className="max-w-6xl w-full">
        <SlideHeader title="Features" />

        {printAll ? (
          // Print mode: render every tab's content in sequence so it's all
          // present in the DOM (and searchable) instead of only the active tab.
          <div className="space-y-20">
            {tabs.map((tab, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-12 items-start">
                <div>
                  <h3 className="text-4xl font-black mb-6" style={{ color: colors.white }}>
                    {tab.icon} {tab.label}
                  </h3>
                  <p className="text-lg mb-8" style={{ color: colors.slate }}>
                    {tab.copy}
                  </p>
                  <div className="space-y-4">
                    {tab.bullets.map((bullet, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <CheckCircle2 size={20} style={{ color: colors.lime }} />
                        <span style={{ color: colors.slate }}>{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="p-6 rounded-2xl border"
                  style={{
                    backgroundColor: colors.glassBg,
                    borderColor: colors.border,
                  }}
                >
                  {tab.demo}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-wrap justify-center gap-2 mb-12"
            >
              {tabs.map((tab, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all ${activeTab === i ? 'text-white' : 'text-gray-400'
                    }`}
                  style={{
                    backgroundColor:
                      activeTab === i
                        ? colors.purple
                        : 'transparent',
                    border: activeTab === i ? 'none' : `1px solid ${colors.border}`,
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </motion.div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="grid md:grid-cols-2 gap-12 items-start"
              >
                <div>
                  <h3 className="text-4xl font-black mb-6" style={{ color: colors.white }}>
                    {tabs[activeTab].label}
                  </h3>
                  <p className="text-lg mb-8" style={{ color: colors.slate }}>
                    {tabs[activeTab].copy}
                  </p>
                  <div className="space-y-4">
                    {tabs[activeTab].bullets.map((bullet, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle2 size={20} style={{ color: colors.lime }} />
                        <span style={{ color: colors.slate }}>{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="p-6 rounded-2xl border"
                  style={{
                    backgroundColor: colors.glassBg,
                    borderColor: colors.border,
                  }}
                >
                  {tabs[activeTab].demo}
                </div>
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </div>
    </section>
  );
};

// Slide 7: Competitive Analysis (REMOVED FOR SALES DECK)

// Slide 6: ROI / Business Impact
const ROISlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="Business Impact" />

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {[
          {
            metric: '75%',
            detail: 'Faster policy cycles',
            icon: '⚡',
          },
          {
            metric: '98%',
            detail: 'Policy acknowledgment rate',
            icon: '✓',
          },
          {
            metric: '80%',
            detail: 'Audit preparation time saved',
            icon: '📋',
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.15 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl border text-center"
            style={{
              backgroundColor: colors.glassBg,
              borderColor: colors.border,
            }}
          >
            <div className="text-5xl mb-4">{item.icon}</div>
            <div
              className="text-5xl font-black mb-2"
              style={{ color: colors.lime }}
            >
              {item.metric}
            </div>
            <p style={{ color: colors.slate }}>{item.detail}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="p-8 rounded-2xl border"
        style={{
          backgroundColor: 'rgba(168,224,99,0.1)',
          borderColor: colors.lime,
        }}
      >
        <h3 className="text-2xl font-bold mb-4" style={{ color: colors.lime }}>
          Bottom Line Impact
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: 'Risk Reduction', desc: 'Stay ahead of audits and regulatory changes' },
            { title: 'Operational Efficiency', desc: 'Cut compliance overhead by 70%+' },
            { title: 'Faster Time to Market', desc: 'Deploy new policies in days, not weeks' },
          ].map((item, i) => (
            <div key={i}>
              <p className="font-semibold mb-2" style={{ color: colors.white }}>
                {item.title}
              </p>
              <p className="text-sm" style={{ color: colors.slate }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// Slide 9: Product Roadmap
const RoadmapSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="Product Roadmap" />

      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            period: 'Today',
            icon: '✓',
            items: ['Core policy management', 'AI-powered drafting', 'Approval workflows', 'Basic reporting'],
          },
          {
            period: '12–18 Months',
            icon: '🚀',
            items: ['SOC2 Type II & ISO 27001', 'Advanced analytics', 'Predictive compliance', 'Integrations (Slack, Google Meet)'],
          },
          {
            period: '24 Months',
            icon: '🔮',
            items: ['Governance intelligence', 'Industry-specific packs', 'Risk Management', 'Partner ecosystem'],
            comingSoon: true,
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.15 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl border relative"
            style={{
              backgroundColor: colors.glassBg,
              borderColor: item.comingSoon ? colors.dimSlate : colors.border,
              opacity: item.comingSoon ? 0.6 : 1,
            }}
          >
            {item.comingSoon && (
              <div
                className="absolute -top-3 -right-3 px-3 py-1 rounded-full text-xs font-bold"
                style={{
                  backgroundColor: colors.purple,
                  color: colors.white,
                }}
              >
                COMING SOON
              </div>
            )}
            <h3 className="text-3xl font-black mb-6" style={{ color: colors.white }}>
              {item.icon} {item.period}
            </h3>
            <ul className="space-y-3">
              {item.items.map((feature, j) => (
                <li key={j} className="flex items-start gap-2 text-sm" style={{ color: colors.slate }}>
                  <span style={{ color: colors.lime }} className="mt-1">
                    ▸
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Slide 10: Traction
const TractionSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="Proven Traction" />

      <div className="grid md:grid-cols-4 gap-6 mb-12">
        {[
          { stat: '75%', label: 'Faster policy cycles' },
          { stat: '98%', label: 'Policy acknowledgment rate' },
          { stat: '30', label: 'Demos conducted' },
          { stat: '2', label: 'Paying customers' },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl text-center border"
            style={{
              backgroundColor: colors.glassBg,
              borderColor: colors.border,
            }}
          >
            <div
              className="text-5xl font-black mb-2"
              style={{ color: colors.lime }}
            >
              {item.stat}
            </div>
            <p className="text-sm" style={{ color: colors.dimSlate }}>
              {item.label}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {[
          {
            title: 'Sortitt',
            desc: '30 global headcount, 680+ freelancers',
            result: '75% reduction in policy cycle time',
            status: 'Converting to paid',
          },
          {
            title: 'Idenfo',
            desc: '$4B assets under management, 30K+ downloads',
            result: '98% policy acknowledgment rate',
            status: 'Expanding to new departments',
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: i === 0 ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl border"
            style={{
              backgroundColor: colors.glassBg,
              borderColor: colors.border,
            }}
          >
            <h3 className="text-2xl font-bold mb-3" style={{ color: colors.white }}>
              {item.title}
            </h3>
            <p className="text-sm mb-4" style={{ color: colors.dimSlate }}>
              {item.desc}
            </p>
            <div
              className="px-4 py-3 rounded-lg mb-4"
              style={{ backgroundColor: 'rgba(168,224,99,0.1)' }}
            >
              <p className="font-semibold text-sm" style={{ color: colors.lime }}>
                {item.result}
              </p>
            </div>
            <p className="text-xs" style={{ color: colors.slate }}>
              Status: {item.status}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Slide 11: Business Model (REMOVED FOR SALES DECK)

// Slide 8: Implementation & Support
const ImplementationSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-6xl w-full">
      <SlideHeader title="Implementation & Support" />

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {[
          {
            phase: 'Phase 1',
            title: 'Onboarding & Setup',
            items: ['Intake & scoping', 'System configuration', 'User provisioning', 'Initial data migration'],
          },
          {
            phase: 'Phase 2',
            title: 'Regulation & Knowledge Ingestion',
            items: ['Region-specific compliance mapping', 'Industry guideline integration', 'Policy framework setup', 'Custom template creation'],
          },
          {
            phase: 'Phase 3',
            title: 'Training & Enablement',
            items: ['Team training sessions', 'Best practices workshop', 'Process documentation', 'Go-live preparation'],
          },
          {
            phase: 'Ongoing',
            title: 'Support & Optimization',
            items: ['24/7 support', 'Quarterly business reviews', 'Continuous improvement', 'New regulation alerts'],
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl border"
            style={{
              backgroundColor: colors.glassBg,
              borderColor: colors.border,
            }}
          >
            <div
              className="text-sm font-bold mb-2 px-3 py-1 rounded-full w-fit"
              style={{
                backgroundColor: 'rgba(168,224,99,0.2)',
                color: colors.lime,
              }}
            >
              {item.phase}
            </div>
            <h3 className="text-xl font-bold mb-3" style={{ color: colors.white }}>
              {item.title}
            </h3>
            <ul className="space-y-2">
              {item.items.map((line, j) => (
                <li key={j} className="text-sm flex items-center gap-2" style={{ color: colors.slate }}>
                  <span style={{ color: colors.lime }}>✓</span> {line}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Slide 9: Contact Us
const ContactSlide = () => (
  <section
    className="relative min-h-screen flex items-center justify-center px-8"
    style={{ backgroundColor: colors.navy }}
  >
    <div className="max-w-5xl w-full text-center">
      <SlideHeader title="Let's Talk" />
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-xl max-w-3xl mx-auto mb-12 text-left md:text-center"
        style={{ color: colors.slate }}
      >
        ManagEaze isn't just another GRC tool — we're building the intelligence layer that
        helps organizations turn policy into practice. With AI at our core and execution as
        our focus, we're positioned to capture significant share of a massive, underserved
        market. The opportunity is massive. The timing is perfect. The team is proven.
        Let's discuss how you can be part of this journey.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        viewport={{ once: true }}
        className="inline-block text-left p-8 rounded-2xl border"
        style={{ backgroundColor: colors.glassBg, borderColor: colors.border }}
      >
        <h3 className="text-sm font-bold uppercase tracking-wide mb-4" style={{ color: colors.dimSlate }}>
          Contact Us
        </h3>
        <ul className="space-y-2">
          <li className="text-lg font-semibold" style={{ color: colors.white }}>
            Faisal Chishti, CEO
          </li>
          <li className="text-lg" style={{ color: colors.lime }}>
            faisal@manageaze.com
          </li>
          <li className="text-lg" style={{ color: colors.slate }}>
            manageaze.com
          </li>
          <li className="text-lg" style={{ color: colors.slate }}>
            Schedule a demo: calendly.com/manageaze
          </li>
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
        className="mt-10"
      >
        <a
          href="mailto:info@manageaze.com"
          className="text-2xl font-black"
          style={{ color: colors.white }}
        >
          info@manageaze.com
        </a>
      </motion.div>
    </div>
  </section>
);

// Slide 13: Team
const TeamSlide = () => {
  const team = [
    { name: 'Faisal Chishti', role: 'CEO', img: '/team/faisal.jpeg', location: 'New York, USA' },
    { name: 'Saad Vakil', role: 'Tech Lead', img: '/team/saadv.png', location: 'Lahore, Pakistan' },
    { name: 'Saad Hasnain', role: 'Product Lead', img: '/team/saad hasnain.jpg', location: 'Lahore, Pakistan' },
    { name: 'Zoya', role: 'Operations', img: '/team/zoya.jpeg', location: 'Global' },
  ];

  return (
    <section
      className="relative min-h-screen flex items-center justify-center px-8"
      style={{ backgroundColor: colors.navy }}
    >
      <div className="max-w-6xl w-full">
        <SlideHeader title="Your Success Partner" />
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-lg text-center mb-12"
          style={{ color: colors.slate }}
        >
          Meet the team dedicated to making your compliance program a competitive advantage
        </motion.p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div
                className="w-full aspect-square rounded-2xl mb-4 border overflow-hidden"
                style={{
                  backgroundColor: colors.glassBg,
                  borderColor: colors.border,
                }}
              >
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-bold text-lg" style={{ color: colors.white }}>
                {member.name}
              </h3>
              <p className="text-sm mb-2" style={{ color: colors.lime }}>
                {member.role}
              </p>
              <p className="text-xs" style={{ color: colors.dimSlate }}>
                {member.location}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Presentation Component
// Print/export CSS: forces a page per slide, disables the framer-motion
// hidden/offscreen states (whileInView never fires for slides that were
// never scrolled into view on a live page), and keeps dark backgrounds
// when the browser prints.
const PrintStyles = () => (
  <style>{`
    @media print {
      @page { size: A4 landscape; margin: 0; }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
    .print-deck * {
      opacity: 1 !important;
      transform: none !important;
      animation: none !important;
      transition: none !important;
    }
    /* min-h-screen resolves against the live viewport, not the print page —
       that's what was producing huge blank pages. Let each slide size to
       its own content instead. */
    .print-deck section.min-h-screen {
      min-height: 0 !important;
    }
    /* Start each slide on a new page, but let long slides (e.g. Features
       with every tab expanded) flow across multiple physical pages rather
       than being clipped or forced into one. */
    .print-slide {
      break-before: page;
      page-break-before: always;
    }
    .print-slide:first-child {
      break-before: avoid;
      page-break-before: avoid;
    }
    /* Keep cards/boxes intact instead of splitting them across a page break. */
    .print-deck [class*="rounded-2xl"] {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  `}</style>
);

export default function PresentSales() {
  const [current, setCurrent] = useState(0);
  const [searchParams] = useSearchParams();
  const printMode = searchParams.get('print') === '1';

  useEffect(() => {
    if (printMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setCurrent((p) => (p + 1) % slides.length);
      if (e.key === 'ArrowLeft') setCurrent((p) => (p - 1 + slides.length) % slides.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [printMode]);

  const slideComponents: { [key: string]: React.ReactNode } = {
    cover: <CoverSlide />,
    problem: <ProblemSlide />,
    solution: <SolutionSlide />,
    'how-it-works': <HowItWorksSlide />,
    'data-import': <DataImportSlide />,
    features: <FeaturesSlide />,
    roi: <ROISlide />,
    traction: <TractionSlide />,
    roadmap: <RoadmapSlide />,
    implementation: <ImplementationSlide />,
    team: <TeamSlide />,
    contact: <ContactSlide />,
  };

  if (printMode) {
    const printSlideComponents = { ...slideComponents, features: <FeaturesSlide printAll /> };
    return (
      <div className="print-deck" style={{ backgroundColor: colors.navy, color: colors.white }}>
        <PrintStyles />
        <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
          <button
            onClick={() => window.print()}
            className="px-4 py-3 rounded-full font-semibold flex items-center gap-2 shadow-lg"
            style={{ backgroundColor: colors.lime, color: colors.navy }}
          >
            <Download size={18} />
            Save as PDF
          </button>
        </div>
        {slides.map((id) => (
          <div
            key={id}
            data-slide-id={id}
            data-document-role="page"
            data-label={slideNames[id]}
            className="print-slide"
          >
            {printSlideComponents[id]}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: colors.navy, color: colors.white }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.4 }}
        >
          {slideComponents[slides[current]]}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div
        className="fixed bottom-0 left-0 right-0 p-6 flex items-center justify-between"
        style={{ backgroundColor: 'rgba(7,8,26,0.95)' }}
      >
        <div style={{ color: colors.slate }}>
          {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </div>

        <div className="flex items-center gap-4">
          {/* PDF Export */}
          <a
            href="?print=1"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 rounded-full transition-all hover:opacity-70 flex items-center gap-2"
            style={{ backgroundColor: colors.lime, color: colors.navy }}
            title="Open searchable print/PDF view"
          >
            <Download size={18} />
            <span className="text-sm font-semibold hidden sm:inline">PDF</span>
          </a>

          {/* Progress dots */}
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-2 rounded-full transition-all ${i === current ? 'w-8' : 'w-2'
                  }`}
                style={{
                  backgroundColor:
                    i === current ? colors.lime : colors.dimSlate,
                }}
              />
            ))}
          </div>

        </div>

        <div style={{ color: colors.slate }} className="text-sm">
          {slideNames[slides[current]]}
        </div>
      </div>
    </div>
  );
}
